import React, { useState, useEffect } from "react";
import { useLanguage } from '../context/LanguageContext';
import adminApi from '../adminApi';
import './TaxChange.css';

// ---- Helper Components ----
const Spinner = () => <div className="spinner"></div>;
const AlertIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>;
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>;

function TaxChange() {
  const { t } = useLanguage();
  const [currentTax, setCurrentTax] = useState<number>(19.0);
  const [newTaxInput, setNewTaxInput] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentTaxRate();
  }, []);

  const fetchCurrentTaxRate = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/tax-rate');
      setCurrentTax(response.data.rate || 19.0);
    } catch (err) {
      console.error('Error fetching tax rate:', err);
      setError(t("Failed to load current tax rate"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTax = async (e: React.FormEvent) => {
    e.preventDefault();

    setError(null);
    setSuccessMessage(null);

    // Validation
    const newTaxValue = parseFloat(newTaxInput);
    if (isNaN(newTaxValue)) {
      setError(t("Please enter a valid number."));
      return;
    }
    if (newTaxValue < 0 || newTaxValue > 100) {
      setError(t("Tax rate must be between 0 and 100."));
      return;
    }
    if (newTaxValue === currentTax) {
      setError(t("New rate is the same as current rate."));
      return;
    }

    // Confirmation
    const isConfirmed = window.confirm(
      `${t("Are you sure you want to change the tax rate from")} ${currentTax}% ${t("to")} ${newTaxValue}%?`
    );

    if (isConfirmed) {
      setIsUpdating(true);
      try {
        const response = await adminApi.put('/tax-rate', {
          rate: newTaxValue,
          changed_by: 'Admin User'
        });

        if (response.data.success) {
          setCurrentTax(newTaxValue);
          setSuccessMessage(`${t("Tax rate successfully updated to")} ${newTaxValue}%.`);
          setNewTaxInput("");
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      } catch (err) {
        console.error('Error updating tax rate:', err);
        setError(t("Failed to update tax rate"));
      } finally {
        setIsUpdating(false);
      }
    }
  };

  if (loading) {
    return <div className="loading">{t("Loading tax rate...")}</div>;
  }

  return (
    <div className="tax-change-container">
      <div className="tax-change-header">
        <h2 className="tax-change-title">{t("Tax Rate Management")}</h2>
        <p className="tax-change-subtitle">
          {t("Manage the global VAT/tax rate applied in the configurator.")}
        </p>
      </div>

      <div className="current-tax-display">
        <span>{t("Current Tax Rate")}</span>
        <div className="current-tax-value">{currentTax.toFixed(1)}%</div>
      </div>

      <form className="tax-change-form" onSubmit={handleUpdateTax}>
        <div className="form-group">
          <label htmlFor="newTaxInput">{t("New Tax Rate (%)")}</label>
          <input
            id="newTaxInput"
            type="number"
            step="0.1"
            placeholder={t("e.g. 23")}
            value={newTaxInput}
            onChange={(e) => setNewTaxInput(e.target.value)}
            disabled={isUpdating}
            className={error ? 'input-error' : ''}
          />
        </div>

        {error && (
          <div className="message message-error">
            <AlertIcon /> {error}
          </div>
        )}
        {successMessage && (
          <div className="message message-success">
            <CheckIcon /> {successMessage}
          </div>
        )}

        <div className="tax-change-footer">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isUpdating || !newTaxInput}
          >
            {isUpdating ? (
              <>
                <Spinner /> {t("Updating...")}
              </>
            ) : (
              t("Update Tax Rate")
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TaxChange;